import type { IntlShape } from 'react-intl';
import { X_AT_KEY } from '@alltrails/core/utils/api';
import type List from '@alltrails/types/List';
import ListItems from '../types/ListItems';
import ListItemsByType from '../types/ListItemsByType';

// Magic numbers from ye olden days
export const favoritesListId = 1000;
export const completedListId = 1001;
export const verifiedListId = 1003;

export type ListImageSize = 'small' | 'small_square' | 'medium' | 'medium_square' | 'large' | 'large_square';

export const getListImageUrl = (id: number, baseUrl?: string, size: ListImageSize = 'medium_square'): string =>
  `${baseUrl}/api/alltrails/lists/${id}/profile_photo?key=${X_AT_KEY}&size=${size}`;

export const getListContentDescription = (intl: IntlShape, listItemCount?: number) => {
  if (listItemCount === undefined) {
    return '';
  }
  return intl.formatMessage(
    { defaultMessage: '{count, plural, one {# item} other {# items}}' },
    {
      count: listItemCount
    }
  );
};

/**
 * Get the number of items in a given list
 * @param list The list
 * @param listItemsByType Object that defines which items are in the list
 * @returns The count of the items in the list, based on the listItemsByType if provided, else based on the list's metadata
 */
export const getListItemCount = (list: List, listItemsByType?: ListItemsByType) => {
  if (listItemsByType) {
    let count = 0;
    Object.keys(listItemsByType).forEach(itemType => {
      count += Object.keys(listItemsByType[itemType]).length;
    });
    return count;
  }
  if (list.metadata) {
    return list.metadata.itemsCount;
  }
  // Explicit undefined return means that we don't know how many items are in the list
  return undefined;
};

/**
 * Gets the items in the given list of the given type
 * @param listItems Object that defines which items are in which lists
 * @param listId Id of the list
 * @param itemType Type of the items
 * @returns The items in the given list of the given type, or, an empty object if none exist
 */
export const getItemsInListOfType = (listItems: ListItems, listId: number, itemType: string) => {
  if (listItems && listItems[listId] && listItems[listId][itemType]) {
    return listItems[listId][itemType];
  }
  return {};
};

/**
 * Gets the item in the given list, of the given type, with the given id
 * @param listItems Object that defines which items are in which lists
 * @param listId Id of the list
 * @param itemType Type of the items
 * @param itemId Id of the item
 * @returns The item, if it exists, else null
 */
export const getItemInList = (listItems: ListItems, listId: number, itemType: string, itemId: number) => {
  const items = getItemsInListOfType(listItems, listId, itemType);
  if (items[itemId]) {
    return items[itemId];
  }
  return null;
};

/**
 * Check if an item is in a given list
 * @param listItems Object that defines which items are in which lists
 * @param listId Id of the list
 * @param itemType Type of the item
 * @param itemId Id of the item
 * @returns true if the item is in the list
 */
export const isItemInList = (listItems: ListItems, listId: number, itemType: string, itemId: number) =>
  !!getItemInList(listItems, listId, itemType, itemId);

/**
 * Check if an item has been saved to any list
 * @param listItems Object that defines which items are in which lists
 * @param itemType Type of the item
 * @param itemId Id of the item
 * @returns true if the item is saved to any list other than completed or verified
 */
export const isSaved = (listItems: ListItems, itemType: string, itemId: number) => {
  if (!listItems) {
    return false;
  }
  const listIds = Object.keys(listItems)
    .map(id => Number(id))
    .filter(listId => listId !== completedListId && listId !== verifiedListId);
  return listIds.some(id => isItemInList(listItems, id, itemType, itemId));
};

/**
 * Check if an item is in the completed list
 * @param listItems Object that defines which items are in which lists
 * @param itemType Type of the item
 * @param itemId Id of the item
 * @returns true if the item is saved to the completed list
 */
export const isCompleted = (listItems: ListItems, itemType: string, itemId: number) =>
  listItems && isItemInList(listItems, completedListId, itemType, itemId);

/**
 * Check if an item is in the verified list
 * @param listItems Object that defines which items are in which lists
 * @param itemType Type of the item
 * @param itemId Id of the item
 * @returns true if the item is saved to the verified list
 */
export const isVerified = (listItems: ListItems, itemType: string, itemId: number) =>
  listItems && isItemInList(listItems, verifiedListId, itemType, itemId);

/**
 * filterLists filters out lists that are not user-built-in
 * @param lists List of lists
 */
export const filterLists = (lists: List[] = []) => lists.filter(list => list.type !== 'user-built-in');

export const MAX_LIST_TITLE_LENGTH = 100;
export const MAX_LIST_DESCRIPTION_LENGTH = 5000;
